import type { JsonValue } from "@e308/core";
import { INFRASTRUCTURE_TARGETS } from "./combat-types.js";
import type {
  DamageTally,
  DiplomacyState,
  EventSetting,
  IncidentAnnouncement,
  MetalShipment,
  PactAction,
  PactState,
} from "./diplomacy-types.js";
import { formatMicro, microFromString } from "./exact-numbers.js";
import { exactRecordKeys as exactKeys, recordObject } from "./record-validation.js";

export const INCIDENT_INTERVAL_MS = 600_000;
export const INCIDENT_WARNING_MS = 60_000;
export const PACT_NOTICE_MS = 60_000;
export const SHIPMENT_TRAVEL_MS = 30_000;
export const DAMAGE_SUMMARY_MS = 5_000;

export function initialDiplomacy(eventSetting: EventSetting): DiplomacyState & JsonValue {
  return cloneDiplomacy({
    eventSetting,
    nextShipmentId: 1,
    pendingActions: [],
    pacts: [],
    shipments: [],
    milestones: [],
    damageWindowEndsAtMs: DAMAGE_SUMMARY_MS,
    damage: [],
    nextIncidentAtMs: INCIDENT_INTERVAL_MS,
    announcedIncident: null,
  }) as DiplomacyState & JsonValue;
}

export function pairKey(left: string, right: string): string {
  return left < right ? `${left}:${right}` : `${right}:${left}`;
}

export function pactFor(state: DiplomacyState, left: string, right: string): PactState | undefined {
  const key = pairKey(left, right);
  return state.pacts.find((pact) => pairKey(pact.left, pact.right) === key);
}

export function pactBlocksCombat(state: DiplomacyState, left: string, right: string): boolean {
  const pact = pactFor(state, left, right);
  return pact?.status === "active" || pact?.status === "notice";
}

export function appendPactAction(
  state: DiplomacyState,
  action: PactAction,
): DiplomacyState & JsonValue {
  return cloneDiplomacy({
    ...state,
    pendingActions: [...state.pendingActions, action],
  }) as DiplomacyState & JsonValue;
}

export function cloneDiplomacy(state: DiplomacyState): DiplomacyState {
  return Object.freeze({
    ...state,
    pendingActions: Object.freeze(state.pendingActions.map((entry) => Object.freeze({ ...entry }))),
    pacts: Object.freeze(
      state.pacts.map((pact) =>
        Object.freeze({
          ...pact,
          proposals: Object.freeze(pact.proposals.map((entry) => Object.freeze({ ...entry }))),
        }),
      ),
    ),
    shipments: Object.freeze(state.shipments.map((entry) => Object.freeze({ ...entry }))),
    milestones: Object.freeze([...state.milestones]),
    damage: Object.freeze(state.damage.map((entry) => Object.freeze({ ...entry }))),
    announcedIncident: state.announcedIncident
      ? Object.freeze({ ...state.announcedIncident })
      : null,
  });
}

export function observeDiplomacy(state: DiplomacyState, seatId: string): DiplomacyState {
  return cloneDiplomacy({
    ...state,
    pendingActions: [],
    pacts: state.pacts.filter(
      (pact) => pact.status !== "open" || pact.left === seatId || pact.right === seatId,
    ),
  });
}

export function validateDiplomacyForSetup(
  value: unknown,
  eventSetting: EventSetting,
  seatIds: readonly string[],
): DiplomacyState & JsonValue {
  const source = recordObject(value, "diplomacy state");
  exactKeys(source, [
    "eventSetting",
    "nextShipmentId",
    "pendingActions",
    "pacts",
    "shipments",
    "milestones",
    "damageWindowEndsAtMs",
    "damage",
    "nextIncidentAtMs",
    "announcedIncident",
  ]);
  if (source.eventSetting !== eventSetting) throw new TypeError("Diplomacy setting is not locked");
  positiveInteger(source.nextShipmentId, "next shipment ID");
  const ids = new Set(seatIds);
  const state: DiplomacyState = {
    eventSetting,
    nextShipmentId: source.nextShipmentId as number,
    pendingActions: array(source.pendingActions, "pending pact actions").map((entry) =>
      validateAction(entry, ids),
    ),
    pacts: array(source.pacts, "pacts").map((entry) => validatePact(entry, ids)),
    shipments: array(source.shipments, "shipments").map((entry) => validateShipment(entry, ids)),
    milestones: validateMilestones(source.milestones),
    damageWindowEndsAtMs: safeTime(source.damageWindowEndsAtMs),
    damage: array(source.damage, "damage tallies").map((entry) => validateDamage(entry, ids)),
    nextIncidentAtMs: safeTime(source.nextIncidentAtMs),
    announcedIncident:
      source.announcedIncident === null ? null : validateIncident(source.announcedIncident, ids),
  };
  validateUniqueState(state);
  return cloneDiplomacy(state) as DiplomacyState & JsonValue;
}

function validateAction(value: unknown, ids: ReadonlySet<string>): PactAction {
  const source = recordObject(value, "pact action");
  exactKeys(source, ["actorId", "targetId", "kind", "message"]);
  pair(source.actorId, source.targetId, ids);
  if (!["propose", "accept", "end"].includes(source.kind as string))
    throw new TypeError("Invalid pact action");
  if (
    source.message !== null &&
    (typeof source.message !== "string" || source.message.length > 128)
  )
    throw new TypeError("Invalid pact message");
  return source as unknown as PactAction;
}

function validatePact(value: unknown, ids: ReadonlySet<string>): PactState {
  const source = recordObject(value, "pact");
  exactKeys(source, ["left", "right", "status", "proposals", "noticeEndsAtMs"]);
  pair(source.left, source.right, ids, true);
  if (!["open", "active", "notice"].includes(source.status as string))
    throw new TypeError("Invalid pact status");
  const proposals = array(source.proposals, "pact proposals").map((entry) => {
    const proposal = recordObject(entry, "pact proposal");
    exactKeys(proposal, ["from", "message"]);
    if (
      (proposal.from !== source.left && proposal.from !== source.right) ||
      (proposal.message !== null &&
        (typeof proposal.message !== "string" || proposal.message.length > 128))
    )
      throw new TypeError("Invalid pact proposal");
    return proposal as unknown as PactState["proposals"][number];
  });
  if ((source.status === "notice") !== (source.noticeEndsAtMs !== null))
    throw new TypeError("Invalid pact notice deadline");
  if (
    (source.status === "open" && proposals.length === 0) ||
    (source.status !== "open" && proposals.length > 0)
  )
    throw new TypeError("Invalid proposals for pact status");
  if (source.noticeEndsAtMs !== null) safeTime(source.noticeEndsAtMs);
  return { ...source, proposals } as unknown as PactState;
}

function validateShipment(value: unknown, ids: ReadonlySet<string>): MetalShipment {
  const source = recordObject(value, "Metal shipment");
  exactKeys(source, ["id", "senderId", "recipientId", "amount", "deliversAtMs", "status"]);
  if (typeof source.id !== "string" || !/^shipment-[1-9][0-9]*$/.test(source.id))
    throw new TypeError("Invalid shipment ID");
  pair(source.senderId, source.recipientId, ids);
  if (typeof source.amount !== "string" || microFromString(source.amount) <= 0n)
    throw new TypeError("Invalid shipment amount");
  safeTime(source.deliversAtMs);
  if (!["in-transit", "delivered", "lost"].includes(source.status as string))
    throw new TypeError("Invalid shipment status");
  return {
    ...source,
    amount: formatMicro(microFromString(source.amount) as never),
  } as MetalShipment;
}

function validateDamage(value: unknown, ids: ReadonlySet<string>): DamageTally {
  const source = recordObject(value, "damage tally");
  exactKeys(source, [
    "attackerId",
    "targetId",
    "effectiveTarget",
    "shieldAbsorbed",
    "troopDamage",
    "infrastructureDamage",
    "blastDoorsActive",
  ]);
  pair(source.attackerId, source.targetId, ids);
  if (!INFRASTRUCTURE_TARGETS.includes(source.effectiveTarget as never))
    throw new TypeError("Invalid damage target");
  for (const key of ["shieldAbsorbed", "troopDamage", "infrastructureDamage"] as const)
    if (typeof source[key] !== "string" || microFromString(source[key]) < 0n)
      throw new TypeError("Invalid damage quantity");
  if (typeof source.blastDoorsActive !== "boolean") throw new TypeError("Invalid damage report");
  return source as unknown as DamageTally;
}

function validateIncident(value: unknown, ids: ReadonlySet<string>): IncidentAnnouncement {
  const source = recordObject(value, "incident announcement");
  exactKeys(source, ["id", "targetId", "effect", "announcedAtMs", "resolvesAtMs"]);
  if (!["meteor-warning", "valley-salvage-train", "clear-skies"].includes(source.id as string))
    throw new TypeError("Invalid incident ID");
  if (
    source.targetId !== null &&
    (typeof source.targetId !== "string" || !ids.has(source.targetId))
  )
    throw new TypeError("Invalid incident target");
  if (
    !["200-power-generator-damage", "100-metal-each", "50-shield-charge-each"].includes(
      source.effect as string,
    )
  )
    throw new TypeError("Invalid incident effect");
  safeTime(source.announcedAtMs);
  safeTime(source.resolvesAtMs);
  return source as unknown as IncidentAnnouncement;
}

function validateMilestones(value: unknown): readonly string[] {
  if (!Array.isArray(value) || !value.every((entry) => typeof entry === "string"))
    throw new TypeError("Invalid milestones");
  if (new Set(value).size !== value.length) throw new TypeError("Duplicate milestone");
  return value;
}

function validateUniqueState(state: DiplomacyState): void {
  if (
    new Set(state.pacts.map((pact) => pairKey(pact.left, pact.right))).size !== state.pacts.length
  )
    throw new TypeError("Duplicate pact pair");
  if (new Set(state.shipments.map((shipment) => shipment.id)).size !== state.shipments.length)
    throw new TypeError("Duplicate shipment ID");
  const greatestShipmentId = state.shipments.reduce(
    (greatest, shipment) => Math.max(greatest, Number(shipment.id.slice(9))),
    0,
  );
  if (state.nextShipmentId <= greatestShipmentId)
    throw new TypeError("Next shipment ID must exceed existing IDs");
}

function pair(left: unknown, right: unknown, ids: ReadonlySet<string>, canonical = false): void {
  if (typeof left !== "string" || typeof right !== "string" || left === right)
    throw new TypeError("Invalid seat pair");
  if (!ids.has(left) || !ids.has(right) || (canonical && left > right))
    throw new TypeError("Invalid seat pair");
}

function array(value: unknown, label: string): unknown[] {
  if (!Array.isArray(value)) throw new TypeError(`Invalid ${label}`);
  return value;
}

function safeTime(value: unknown): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) throw new TypeError("Invalid time");
  return value as number;
}

function positiveInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 1)
    throw new TypeError(`Invalid ${label}`);
  return value as number;
}
